import { useState, type FormEvent } from "react";
import { useI18n } from "../../i18n/I18nProvider";
import type { MessageKey } from "../../i18n/en";
import { Button } from "../../components/ui/Button";
import { ErrorText } from "../../components/ui/feedback";
import { Field } from "../../components/ui/Field";
import { Input } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";
import type { PersonInput } from "./mutations";

export interface InvitePlayerModalProps {
  busy: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: (input: PersonInput) => void;
}

const MAX_NAME = 80;
const MAX_NUMBER = 999;

/** Empty is no number. Otherwise a whole number from 0 to 999; anything else is undefined. */
export function parseNumberField(raw: string): number | null | undefined {
  const text = raw.trim();
  if (text === "") return null;
  if (!/^\d+$/.test(text)) return undefined;
  const value = Number(text);
  return value <= MAX_NUMBER ? value : undefined;
}

/** Creates a person and their player link in one step. The caller shows the new link. */
export function InvitePlayerModal({ busy, error, onClose, onSubmit }: InvitePlayerModalProps) {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const [problem, setProblem] = useState<MessageKey | null>(null);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = name.trim();
    const parsed = parseNumberField(number);
    if (trimmed === "" || trimmed.length > MAX_NAME) {
      setProblem("person.invalidName");
      return;
    }
    if (parsed === undefined) {
      setProblem("person.invalidNumber");
      return;
    }
    setProblem(null);
    onSubmit({ name: trimmed, number: parsed });
  };

  return (
    <Modal title={t("roster.inviteTitle")} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Field
          label={t("person.name")}
          error={problem === "person.invalidName" ? t(problem) : undefined}
        >
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoComplete="off"
            />
          )}
        </Field>
        <Field
          label={t("person.number")}
          hint={t("person.numberHint")}
          error={problem === "person.invalidNumber" ? t(problem) : undefined}
        >
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              inputMode="numeric"
              value={number}
              onChange={(event) => setNumber(event.target.value)}
            />
          )}
        </Field>
        <ErrorText error={error} />
        <div className="flex justify-end gap-2">
          <Button type="submit" variant="primary" disabled={busy}>
            {t("roster.inviteSubmit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
